import { useEffect, useRef, type InputHTMLAttributes, type JSX, type MouseEvent } from 'react';
import { useRangeHintSeen } from '@/hooks/useRangeSelection';
import type { CheckState } from '@/lib/rangeSelect';
import { cn } from '@/lib/utils';

/**
 * Bulk-selection bits shared by every list with per-row checkboxes. The
 * behaviour lives in `useRangeSelection`; these are the pieces of markup.
 */

/** "Tip: Shift-click to select a range" — next to a bulk bar, until a range has been used once. */
export function RangeSelectHint({ show = true, className }: { show?: boolean; className?: string }): JSX.Element | null {
  const seen = useRangeHintSeen();
  if (!show || seen) return null;
  return (
    <span className={cn('text-xs text-muted-foreground', className)}>
      Tip: <kbd className="font-sans font-medium">Shift</kbd>-click to select a range
    </span>
  );
}

/** Header "select all" checkbox: indeterminate while some, not all, are selected. */
export function SelectAllCheckbox({
  state,
  onToggle,
  label,
  disabled,
  className,
}: {
  state: CheckState;
  onToggle: () => void;
  label: string;
  disabled?: boolean;
  className?: string;
}): JSX.Element {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = state === 'some';
  }, [state]);
  return (
    <input
      ref={ref}
      type="checkbox"
      aria-label={label}
      className={cn('h-4 w-4 cursor-pointer accent-[var(--brand)] disabled:cursor-not-allowed', className)}
      checked={state === 'all'}
      disabled={disabled}
      onChange={onToggle}
    />
  );
}

/**
 * A row checkbox with a padded hit area: the label fills the cell, so a
 * near-miss still ticks it. `title` (the reason a row can't be selected)
 * sits on the label, because disabled inputs don't reliably show tooltips.
 */
export function RowCheckbox({
  onMouseDown,
  title,
  className,
  hitClassName,
  disabled,
  ...input
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'onMouseDown'> & {
  onMouseDown?: (e: MouseEvent<HTMLElement>) => void;
  hitClassName?: string;
}): JSX.Element {
  return (
    <label
      className={cn(
        'inline-flex shrink-0 items-center justify-center p-2 -m-2',
        disabled ? 'cursor-not-allowed' : 'cursor-pointer',
        hitClassName,
      )}
      title={title}
      onMouseDown={onMouseDown}
    >
      <input
        type="checkbox"
        disabled={disabled}
        className={cn('h-4 w-4 accent-[var(--brand)] disabled:cursor-not-allowed', !disabled && 'cursor-pointer', className)}
        {...input}
      />
    </label>
  );
}

/** Classes for a row that is itself a selection target (see `useRangeSelection().rowProps`). */
export function selectableRowClass(disabled = false): string {
  return cn(
    'focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[var(--brand)]',
    disabled ? 'cursor-not-allowed' : 'cursor-pointer',
  );
}
